import { useState, useRef, useEffect } from 'react';
import {
  Container,
  Title,
  Text,
  Card,
  Stack,
  Center,
  Badge,
  Group,
  Box,
  ScrollArea,
  Skeleton,
  ActionIcon,
  Progress,
  Loader,
} from '@mantine/core';
import {
  IconCards,
  IconBook,
  IconChevronRight,
  IconArrowDown,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useDecks } from '@/features/flashcards/hooks/useFlashcards';
import { useQueryClient } from '@tanstack/react-query';

interface DeckWithProgress {
  id: string;
  title: string;
  description: string | null;
  card_count: number;
  deck_type: 'vocabulary' | 'custom';
  created_at: string;
  mastered_count?: number;
  learning_count?: number;
  new_count?: number;
}

/**
 * Mobile Flashcards Page
 *
 * Touch-optimized deck selection:
 * - Vertical scrollable deck cards
 * - Progress indicators
 * - Touch-friendly tap targets
 */
export function MobileFlashcardsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: decks, isLoading, refetch } = useDecks({});

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const touchStartY = useRef(0);
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  const handleDeckClick = (deckId: string) => {
    navigate(`/flashcards/review/${deckId}?mode=all`);
  };

  // Keep a ref to pullDistance for the native event handler
  const pullDistanceRef = useRef(pullDistance);
  useEffect(() => {
    pullDistanceRef.current = pullDistance;
  }, [pullDistance]);

  // Pull-to-refresh using native event listeners for proper passive: false support
  useEffect(() => {
    const scrollAreaElement = scrollAreaRef.current;
    if (!scrollAreaElement) return;

    const handleTouchStart = (e: TouchEvent) => {
      const viewport = scrollAreaElement.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement | null;
      const scrollTop = viewport?.scrollTop ?? 0;

      if (scrollTop <= 1) {
        touchStartY.current = e.touches[0].clientY;
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (touchStartY.current === 0) return;

      const viewport = scrollAreaElement.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement | null;
      const scrollTop = viewport?.scrollTop ?? 0;

      if (scrollTop <= 1) {
        const currentY = e.touches[0].clientY;
        const distance = Math.max(0, currentY - touchStartY.current);

        if (distance > 0) {
          e.preventDefault();
          setPullDistance(Math.min(distance, 80));
        }
      }
    };

    const handleTouchEnd = async () => {
      const currentPullDistance = pullDistanceRef.current;
      if (currentPullDistance > 60) {
        setIsRefreshing(true);
        await refetch();
        queryClient.invalidateQueries({ queryKey: ['flashcards'] });
        setTimeout(() => {
          setIsRefreshing(false);
          setPullDistance(0);
        }, 500);
      } else {
        setPullDistance(0);
      }
      touchStartY.current = 0;
    };

    scrollAreaElement.addEventListener('touchstart', handleTouchStart, { passive: true });
    scrollAreaElement.addEventListener('touchmove', handleTouchMove, { passive: false });
    scrollAreaElement.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      scrollAreaElement.removeEventListener('touchstart', handleTouchStart);
      scrollAreaElement.removeEventListener('touchmove', handleTouchMove);
      scrollAreaElement.removeEventListener('touchend', handleTouchEnd);
    };
  }, [refetch, queryClient]);

  if (isLoading) {
    return (
      <Container size="sm" py="md">
        <Stack gap="md">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} height={140} radius="md" />
          ))}
        </Stack>
      </Container>
    );
  }

  return (
    <Box style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box p="md" style={{ borderBottom: '1px solid var(--mantine-color-gray-3)' }}>
        <Title order={2}>My Flashcards</Title>
        <Text size="sm" c="dimmed" mt={4}>
          {decks?.length || 0} {decks?.length === 1 ? 'deck' : 'decks'} •{' '}
          {decks?.reduce((sum, deck) => sum + (deck.card_count || 0), 0) || 0} cards
        </Text>
      </Box>

      {/* Deck List */}
      <ScrollArea
        ref={scrollAreaRef}
        style={{ flex: 1 }}
        offsetScrollbars
      >
        {/* Pull-to-refresh indicator */}
        <Box
          style={{
            height: `${pullDistance}px`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: pullDistance === 0 ? 'height 0.3s ease-out' : 'none',
            overflow: 'hidden',
          }}
        >
          {isRefreshing ? (
            <Loader size="sm" />
          ) : pullDistance > 0 && (
            <IconArrowDown
              size={20}
              style={{
                opacity: pullDistance / 80,
                transform: `rotate(${pullDistance > 60 ? 180 : 0}deg)`,
                transition: 'transform 0.2s',
              }}
            />
          )}
        </Box>

        <Container size="sm" py="md">
          {decks && decks.length > 0 ? (
            <Stack gap="md">
              {decks.map((deck: DeckWithProgress) => {
                const masteredCount = deck.mastered_count || 0;
                const learningCount = deck.learning_count || 0;
                const newCount = deck.new_count || 0;
                const totalCount = deck.card_count || 0;
                const masteredPercentage = totalCount > 0 ? (masteredCount / totalCount) * 100 : 0;

                return (
                  <Card
                    key={deck.id}
                    shadow="sm"
                    padding="lg"
                    radius="md"
                    withBorder
                    onClick={() => handleDeckClick(deck.id)}
                    style={{ cursor: 'pointer', minHeight: '120px' }}
                  >
                    <Stack gap="xs">
                      {/* Title and Icon */}
                      <Group justify="space-between" align="flex-start" wrap="nowrap">
                        <Box style={{ flex: 1, minWidth: 0 }}>
                          <Text fw={600} size="lg" lineClamp={2}>
                            {deck.title}
                          </Text>
                          {deck.description && (
                            <Text size="sm" c="dimmed" mt={4} lineClamp={2}>
                              {deck.description}
                            </Text>
                          )}
                        </Box>
                        <Group gap="xs" style={{ flexShrink: 0 }}>
                          <IconCards size={24} color="var(--mantine-color-teal-6)" />
                          <ActionIcon variant="subtle" color="gray">
                            <IconChevronRight size={20} />
                          </ActionIcon>
                        </Group>
                      </Group>

                      {/* Progress Bar */}
                      {masteredPercentage > 0 && (
                        <Box>
                          <Progress value={masteredPercentage} size="sm" color="teal" />
                          <Text size="xs" c="dimmed" mt={4}>
                            {Math.round(masteredPercentage)}% mastered
                          </Text>
                        </Box>
                      )}

                      {/* Footer with card counts */}
                      <Group justify="space-between" mt="xs">
                        <Group gap="xs">
                          <Badge variant="light" color="gray" size="sm">
                            {totalCount} cards
                          </Badge>
                          {deck.deck_type === 'vocabulary' && (
                            <Badge variant="light" color="blue" size="sm" leftSection={<IconBook size={12} />}>
                              Vocabulary
                            </Badge>
                          )}
                        </Group>
                        {(masteredCount > 0 || learningCount > 0 || newCount > 0) && (
                          <Group gap={4}>
                            {newCount > 0 && (
                              <Badge variant="dot" color="orange" size="sm">
                                {newCount} new
                              </Badge>
                            )}
                            {learningCount > 0 && (
                              <Badge variant="dot" color="yellow" size="sm">
                                {learningCount}
                              </Badge>
                            )}
                            {masteredCount > 0 && (
                              <Badge variant="dot" color="teal" size="sm">
                                {masteredCount}
                              </Badge>
                            )}
                          </Group>
                        )}
                      </Group>
                    </Stack>
                  </Card>
                );
              })}
            </Stack>
          ) : (
            <Center py="xl">
              <Stack align="center" gap="md">
                <IconCards size={48} stroke={1.5} color="var(--mantine-color-gray-5)" />
                <Stack align="center" gap="xs">
                  <Text size="lg" fw={500} c="dimmed">
                    No Decks Yet
                  </Text>
                  <Text size="sm" c="dimmed" ta="center">
                    Create flashcard decks to start studying
                  </Text>
                </Stack>
              </Stack>
            </Center>
          )}
        </Container>
      </ScrollArea>
    </Box>
  );
}
